import { useContext, useMemo } from 'react';
import { TaskLocationsContext, TaskLocationsContextData } from './context';
import { MapUserLocation } from '../../components/user-map-marker';
import { Task } from '@/api';
import { BeaconMapMarkerProps } from '../../components/beacon-map-marker';

export function useTaskLocations(): TaskLocationsContextData {
    const data = useContext<TaskLocationsContextData | undefined>(
        TaskLocationsContext
    );

    if (!data) {
        throw new Error(
            "TaskLocationsContextData unavailable. Are you sure you're using this in a nested component of an TaskLocationsProvider?"
        );
    }

    return data;
}

export function useTaskUserMarker(task: Task): MapUserLocation | null {
    const { locations } = useTaskLocations();

    const userMarker = useMemo(() => {
        let lat: number | undefined;
        let lng: number | undefined;
        let acc: number | undefined;

        if (task.location && locations.length > 0) {
            if (task.location.ts > locations[0].ts) {
                lat = task.location.lat;
                lng = task.location.lng;
                acc = task.location.acc;
            } else {
                lat = locations[0].lat;
                lng = locations[0].lng;
                acc = locations[0].acc;
            }
        } else if (task.location) {
            lat = task.location.lat;
            lng = task.location.lng;
            acc = task.location.acc;
        } else if (locations.length > 0) {
            lat = locations[0].lat;
            lng = locations[0].lng;
            acc = locations[0].acc;
        }

        if (!lat || !lng) return null;
        return {
            userId: task.user?.id,
            taskId: task.id,
            image: task.displayInfo.image,
            lat,
            lng,
            acc
        };
    }, [
        task.id,
        task.location,
        task.user?.id,
        task.displayInfo.image,
        locations
    ]);

    return userMarker;
}

export function useTaskBeaconMarkers(task: Task): BeaconMapMarkerProps[] {
    const beaconMarkers = useMemo<BeaconMapMarkerProps[]>(() => {
        const _updatedAt = task.beaconsUpdatedAt;
        if (!_updatedAt) return [];
        const updatedAt = new Date(_updatedAt);

        const _beaconMarkers: BeaconMapMarkerProps[] = [];

        task.beaconSnapshot.forEach((beacon) => {
            if (beacon.location) {
                _beaconMarkers.push({
                    lat: beacon.location.lat,
                    lng: beacon.location.lng,
                    distanceMeters: beacon.distanceMeters,
                    beacon,
                    updatedAt
                });
            }
        });

        return _beaconMarkers;
    }, [task.beaconSnapshot, task.beaconsUpdatedAt]);

    return beaconMarkers;
}
